import React, { useEffect, useState } from "react";

import { TsicommonCms5, templates } from "@reactivated";

import { getProduct, getProductURL } from "../api/products";
import { CompareGrid } from "../apps/shop-compare/CompareGrid";
import {
    AbstractProductTileData,
    CompareTileType,
} from "../apps/shop-compare/ShopCompareTypes";
import { isoProductID, isoWebPageURL } from "../models/nominals";

type ShopTileWithLink = {
    link?: string;
    title?: string;
    compare_tiles?: CompareTileType[];
} & Partial<AbstractProductTileData>;

export type CompareTileBlockWithDisabled = TsicommonCms5 & {
    disabled: boolean;
};

export type DropdownHeadersType = {
    header: string | undefined;
    id: number | undefined;
    children: CompareTileBlockWithDisabled[] | undefined;
}[];

export type AllTileModelsType = {
    shop_tile_title: string | undefined;
    title: string | undefined;
    product: number | undefined;
    product_link: string | undefined;
};

export type GroupedTileModelsType = Record<string, AllTileModelsType[]>;

const groupModelsByProductLink = (
    models: AllTileModelsType[],
    shopTileLinks: string[],
    props: templates.ShopTileTemplate,
): GroupedTileModelsType => {
    // First group by product_link
    const groupedByLink = models.reduce((groups, model) => {
        if (!model.product || !model.product_link) return groups;

        const productLink = model.product_link;
        if (!groups[productLink]) {
            groups[productLink] = [];
        }
        groups[productLink].push(model);
        return groups;
    }, {} as GroupedTileModelsType);

    // Create a mapping from ShopTile links to ShopTile titles
    const linkToTitleMap = new Map<string, string>();
    props.tileset?.shop_tiles?.value.forEach((shopTile) => {
        const link = (shopTile.value.shop_tile as ShopTileWithLink)?.link;
        const title = shopTile.value.shop_tile?.title;
        if (link && title) {
            linkToTitleMap.set(link, title);
        }
    });

    // Filter out models where product_link doesn't match any ShopTile link
    const filteredGroups: GroupedTileModelsType = {};
    Object.entries(groupedByLink).forEach(([productLink, models]) => {
        const filteredModels = models.filter(
            (model) =>
                model.product_link &&
                shopTileLinks.includes(model.product_link),
        );

        if (filteredModels.length > 0) {
            // Use the ShopTile title that corresponds to this product_link
            const groupTitle = linkToTitleMap.get(productLink) || "Other";
            filteredGroups[groupTitle] = filteredModels;
        }
    });

    return filteredGroups;
};

export const Template = (props: templates.ShopTileTemplate) => {
    const getInitialSelectedCompareTiles = () => {
        const initialSeenProductIds = new Set<number>();
        return props.shop_tile.compare_tiles.value
            .map((t) => t.value?.product || 0)
            .filter((productId) => {
                if (productId && !initialSeenProductIds.has(productId)) {
                    initialSeenProductIds.add(productId);
                    return true;
                }
                return false;
            });
    };

    const [selectedCompareTiles, setSelectedCompareTiles] = useState<number[]>(
        getInitialSelectedCompareTiles(),
    );

    const [allTileModels, setAllTileModels] = useState<AllTileModelsType[]>([]);

    // Fetch product links for all unique product IDs
    useEffect(() => {
        const fetchProductLinks = async () => {
            const uniqueProductIds = new Set<number>();

            // Collect all unique product IDs from the tileset
            props.tileset?.shop_tiles?.value.forEach((shopTile) => {
                shopTile.value.shop_tile?.compare_tiles?.forEach((t) => {
                    const productId = t.value.product;
                    if (productId) {
                        uniqueProductIds.add(productId);
                    }
                });
            });

            // Fetch product data for each unique product ID
            const linkPromises = Array.from(uniqueProductIds).map(
                async (productId) => {
                    try {
                        const productURL = getProductURL(
                            isoProductID.wrap(productId),
                        );
                        const product = await getProduct(productURL);
                        return {
                            productId,
                            link: product.link
                                ? isoWebPageURL.unwrap(product.link)
                                : null,
                        };
                    } catch (error) {
                        console.error(
                            `Failed to fetch product ${productId}:`,
                            error,
                        );
                        return { productId, link: null };
                    }
                },
            );

            const results = await Promise.all(linkPromises);
            const newProductLinks = new Map<number, string>();

            results.forEach(({ productId, link }) => {
                if (link) {
                    newProductLinks.set(productId, link);
                }
            });

            // Now build the allTileModels with the fetched product links
            const tileModels: AllTileModelsType[] = [];
            const tileSeenProductIds = new Set<number>();

            props.tileset?.shop_tiles?.value.forEach((shopTile) => {
                shopTile.value.shop_tile?.compare_tiles?.forEach((t) => {
                    const productId = t.value.product;
                    if (productId && !tileSeenProductIds.has(productId)) {
                        tileSeenProductIds.add(productId);
                        tileModels.push({
                            shop_tile_title: shopTile.value.shop_tile?.title,
                            title: t.value.title || "",
                            product: productId,
                            product_link: newProductLinks.get(productId),
                        });
                    }
                });
            });

            setAllTileModels(tileModels);
        };

        if (props.tileset?.shop_tiles) {
            fetchProductLinks();
        }
    }, [props.tileset?.shop_tiles]);

    // Collect ShopTile links for filtering
    const shopTileLinks: string[] = [];
    props.tileset?.shop_tiles?.value.forEach((shopTile) => {
        const link = (shopTile.value.shop_tile as ShopTileWithLink)?.link;
        if (link) {
            shopTileLinks.push(link);
        }
    });

    if (!props.tileset?.shop_tiles || !props.shop_tile) {
        return null;
    }

    const compareTiles: CompareTileType[] =
        props.tileset.shop_tiles.value.reduce((acc, t) => {
            const tile = t.value.shop_tile;
            acc.push(
                ...(tile?.compare_tiles?.map(
                    (ct) => ct.value as CompareTileType,
                ) || []),
            );
            return acc;
        }, [] as CompareTileType[]);

    const groupedModels = groupModelsByProductLink(
        allTileModels,
        shopTileLinks,
        props,
    );

    const validSelectedTiles = selectedCompareTiles.filter((tileId) => {
        // Check if this tileId exists in any of the grouped models
        return Object.values(groupedModels).some((models) =>
            models.some((model) => model.product === tileId),
        );
    });

    const handleSelectProductTile = (newTile: number, column: number) => {
        setSelectedCompareTiles((prev) => {
            const newTiles = [...prev];
            newTiles[column] = newTile;
            return newTiles;
        });
    };

    return (
        <div>
            <CompareGrid
                compareTiles={compareTiles}
                compareFeatures={
                    props.tileset.compare_features?.value.map(
                        (cf) => cf.value,
                    ) || []
                }
                groupedDropdownModels={groupedModels}
                handleSelectProduct={handleSelectProductTile}
                selectedProducts={validSelectedTiles}
            />
        </div>
    );
};
